import { useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { MAX_WHATSAPP_ALLOWED_RECIPIENTS, type PlatformOrganization, type WhatsAppAllowedRecipientsResponse } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Dialog } from "../../design/components/Dialog";
import { Reason } from "../../design/components/Reason";
import { TextArea } from "../../design/components/TextArea";
import { messageForError } from "../../i18n/errors";
import { useT, type Translate } from "../../i18n/locale";
import { platformWhatsAppAllowedRecipientsQuery, useSaveWhatsAppAllowedRecipients } from "../../whatsapp/settings-api";
import { parseAllowedNumbers } from "./allowed-numbers";
import "./platform.css";

// «Numeri WhatsApp» di un'azienda nella pagina «Aziende di Gioia365» (piano M6, R2, Task 7b; D19, questione 414; senza mock-up,
// sul modello della «Telefonia» di un'azienda, `organization-telephony.tsx`): il Super admin di Gioia365 scrive i numeri verso cui
// i WhatsApp automatici dell'azienda possono partire dove sono richiesti (fuori dalla produzione, o con
// `WHATSAPP_RECIPIENT_ALLOWLIST_REQUIRED=true`): i telefoni del team che prova, al massimo venti, uno per riga. Il pulsante sta
// accanto a «Telefonia» soltanto dove sono richiesti (`whatsappAllowlistRequired` dell'elenco delle aziende, `organizations.tsx`).
// Un elenco vuoto si salva (nessun WhatsApp automatico parte, e la finestra lo dice: un avviso, non un blocco). La versione è quella del collegamento WhatsApp
// dell'azienda (`null` finché non c'è: il primo salvataggio lo crea): quella letta all'apertura, e dopo un `409 version_conflict`
// quella di adesso, con i numeri scritti qui ancora nel campo (come la finestra «Imposta» di R1).

type Props = { organization: PlatformOrganization; onClose: () => void };

/** I numeri come stanno nel campo: uno per riga. */
const asText = (numbers: readonly string[]) => numbers.join("\n");

export function OrganizationWhatsAppDialog({ organization, onClose }: Props) {
  const t = useT();
  const { id: organizationId, name } = organization;
  const query = useQuery(platformWhatsAppAllowedRecipientsQuery(organizationId));
  const data = query.data;

  let content: ReactNode;
  if (data) {
    content = <AllowedRecipientsForm organizationId={organizationId} name={name} saved={data} />;
  } else if (query.isError) {
    content = (
      <div className="g-platform__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, query.error)}
        </Reason>
        <Button onClick={() => void query.refetch()} disabled={query.isFetching} aria-busy={query.isFetching ? "true" : undefined}>
          {t("common.retry")}
        </Button>
      </div>
    );
  } else {
    content = <p className="g-platform__counts">{t("common.loading")}</p>;
  }

  return (
    <Dialog
      open
      size="wide"
      title={t("platformWhatsApp.title", { organization: name })}
      onClose={onClose}
      footer={
        <>
          <span className="g-platform__spacer" />
          <Button onClick={onClose}>{t("platform.manage.done")}</Button>
        </>
      }
    >
      {content}
    </Dialog>
  );
}

type FormProps = { organizationId: string; name: string; saved: WhatsAppAllowedRecipientsResponse };

type Problem = { field: "allowed" | "form"; text: (t: Translate) => string };

function AllowedRecipientsForm({ organizationId, name, saved }: FormProps) {
  const t = useT();
  const ids = useId();
  const allowedId = `${ids}-allowed`;
  const statusId = `${ids}-status`;
  const save = useSaveWhatsAppAllowedRecipients(organizationId);
  // Il campo nasce dall'elenco letto all'apertura e non segue le riletture: ciò che si scrive non sparisce sotto le mani.
  const [text, setText] = useState(() => asText(saved.allowedRecipients));
  const [base, setBase] = useState(() => asText(saved.allowedRecipients));
  const version = useRef<number | null>(saved.version);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [done, setDone] = useState(false);
  const dirty = text.trim() !== base.trim();
  const parsed = parseAllowedNumbers(text, MAX_WHATSAPP_ALLOWED_RECIPIENTS);
  const empty = parsed.ok && parsed.numbers.length === 0;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (save.isPending) return;
    setDone(false);
    if (!parsed.ok) {
      setProblem(
        parsed.problem === "invalid"
          ? { field: "allowed", text: (tr) => tr("platformWhatsApp.allowedInvalid", { number: parsed.number }) }
          : { field: "allowed", text: (tr) => tr("platformWhatsApp.allowedTooMany", { max: MAX_WHATSAPP_ALLOWED_RECIPIENTS }) },
      );
      document.getElementById(allowedId)?.focus();
      return;
    }
    setProblem(null);
    const expectedVersion = version.current;
    try {
      const response = await save.mutateAsync({ allowedRecipients: parsed.numbers, ...(expectedVersion === null ? {} : { expectedVersion }) });
      version.current = response.version;
      const savedText = asText(response.allowedRecipients);
      setText(savedText);
      setBase(savedText);
      setDone(true);
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === "version_conflict") {
        // Il prossimo «Salva» porta la versione di adesso, con i numeri scritti qui.
        const current = error.details?.version;
        if (typeof current === "number" || current === null) version.current = current;
        setProblem({ field: "form", text: (tr) => tr("platformWhatsApp.conflict", { organization: name }) });
      } else {
        setProblem({ field: "form", text: (tr) => messageForError(tr, error) });
      }
    }
  }

  let status: string | null = null;
  if (save.isPending) status = t("common.saving");
  else if (done) status = t("platformWhatsApp.saved");
  else if (!dirty) status = t("platformWhatsApp.saveNothing");

  return (
    <form className="g-stack" noValidate onSubmit={(event) => void submit(event)}>
      <p>{t("platformWhatsApp.intro", { organization: name })}</p>
      <TextArea
        id={allowedId}
        label={t("platformWhatsApp.allowed")}
        value={text}
        rows={5}
        autoComplete="off"
        spellCheck={false}
        hint={t("platformWhatsApp.allowedFormat", { max: MAX_WHATSAPP_ALLOWED_RECIPIENTS })}
        error={problem?.field === "allowed" ? problem.text(t) : undefined}
        onChange={(event) => {
          setText(event.target.value);
          setDone(false);
          setProblem(null);
        }}
      />
      {empty ? <Reason tone="warning">{t("platformWhatsApp.empty", { organization: name })}</Reason> : null}
      {problem?.field === "form" ? (
        <Reason tone="warning" role="alert">
          {problem.text(t)}
        </Reason>
      ) : null}
      <div className="g-telephony__save">
        <Button variant="primary" type="submit" disabled={!dirty || save.isPending} aria-busy={save.isPending ? "true" : undefined} aria-describedby={statusId}>
          {t("platformWhatsApp.save")}
        </Button>
        {/* Sempre nella pagina e vuota finché non serve: uno screen reader annuncia l'esito del salvataggio. */}
        <p id={statusId} className="g-platform__wait" role="status">
          {status}
        </p>
      </div>
    </form>
  );
}
